// Keyboard Shortcuts Hook
import { useEffect } from 'preact/hooks'
import { 
  activeSection, 
  activeDetailTile, 
  SECTIONS,
  switchSection,
  closeDetailTile,
  cycleBasis,
  toggleDomain
} from '../state/globalSignals.js'

/**
 * Keyboard Shortcuts for navigation and controls
 * 
 * Shortcuts:
 * - 1-6: Jump to section (Fact Sheet, Scores, Operation, Mechanical, Chemical, Resistance)
 * - ←/→: Navigate sections (previous/next)
 * - Escape: Close detail panel
 * - B: Cycle basis (abs → grav → vol → abs)
 * - D: Toggle domain (energy-power ↔ capacity-current)
 * - ?: Show help dialog
 * - Cmd/Ctrl+K: Command palette (future)
 */
export function useKeyboardShortcuts() {
  useEffect(() => {
    function handleKeyDown(e) {
      // Ignore if user is typing in input/textarea
      if (
        e.target.tagName === 'INPUT' ||
        e.target.tagName === 'TEXTAREA' ||
        e.target.isContentEditable
      ) {
        return
      }
      
      // 1-6: Jump to section
      if (e.key >= '1' && e.key <= '6') {
        const index = parseInt(e.key) - 1
        if (index < SECTIONS.length) {
          switchSection(SECTIONS[index].id)
          e.preventDefault()
        }
        return
      }
      
      // Escape: Close detail
      if (e.key === 'Escape' && activeDetailTile.value) {
        closeDetailTile()
        e.preventDefault()
        return
      }
      
      // Arrow Left: Previous section
      if (e.key === 'ArrowLeft') {
        const currentIndex = SECTIONS.findIndex(s => s.id === activeSection.value)
        if (currentIndex === -1) return
        const prevIndex = (currentIndex - 1 + SECTIONS.length) % SECTIONS.length
        switchSection(SECTIONS[prevIndex].id)
        e.preventDefault()
        return
      }
      
      // Arrow Right: Next section
      if (e.key === 'ArrowRight') {
        const currentIndex = SECTIONS.findIndex(s => s.id === activeSection.value)
        if (currentIndex === -1) return
        const nextIndex = (currentIndex + 1) % SECTIONS.length
        switchSection(SECTIONS[nextIndex].id)
        e.preventDefault()
        return
      }
      
      // B: Cycle basis
      if (e.key === 'b' || e.key === 'B') {
        cycleBasis()
        e.preventDefault()
        return
      }
      
      // D: Toggle domain
      if (e.key === 'd' || e.key === 'D') {
        toggleDomain()
        e.preventDefault()
        return
      }
      
      // ?: Show keyboard shortcuts help
      if (e.key === '?') {
        showKeyboardHelp()
        e.preventDefault()
        return
      }
      
      // Cmd/Ctrl + K: Command palette (future feature)
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        // openCommandPalette()
        console.log('Command palette: Coming soon!')
        e.preventDefault()
        return
      }
    }
    
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])
}

/**
 * Show keyboard shortcuts help dialog
 */
function showKeyboardHelp() {
  const helpContent = `
    <div className="keyboard-help-dialog">
      <h3>⌨️ Keyboard Shortcuts</h3>
      
      <div className="shortcut-section">
        <h4>Navigation</h4>
        <div className="shortcut-row">
          <div><kbd>1</kbd>-<kbd>6</kbd></div>
          <span>Jump to section (Fact Sheet, Scores, Operation, Mechanical, Chemical, Resistance)</span>
        </div>
        <div className="shortcut-row">
          <div><kbd>←</kbd> <kbd>→</kbd></div>
          <span>Navigate between sections</span>
        </div>
        <div className="shortcut-row">
          <div><kbd>Esc</kbd></div>
          <span>Close detail panel</span>
        </div>
      </div>
      
      <div className="shortcut-section">
        <h4>View Controls</h4>
        <div className="shortcut-row">
          <div><kbd>B</kbd></div>
          <span>Cycle basis: Absolute → Gravimetric → Volumetric</span>
        </div>
        <div className="shortcut-row">
          <div><kbd>D</kbd></div>
          <span>Toggle domain: Energy/Power ↔ Capacity/Current</span>
        </div>
      </div>
      
      <div className="shortcut-section">
        <h4>Other</h4>
        <div className="shortcut-row">
          <div><kbd>?</kbd></div>
          <span>Show this help dialog</span>
        </div>
        <div className="shortcut-row">
          <div><kbd>⌘</kbd>/<kbd>Ctrl</kbd> + <kbd>K</kbd></div>
          <span>Command palette (coming soon)</span>
        </div>
      </div>
      
      <button className="keyboard-help-close" onclick="this.closest('.keyboard-help-overlay').remove()">
        Close <kbd>Esc</kbd>
      </button>
    </div>
  `
  
  // Create overlay
  const overlay = document.createElement('div')
  overlay.className = 'keyboard-help-overlay'
  overlay.innerHTML = helpContent
  overlay.onclick = (e) => {
    if (e.target === overlay) overlay.remove()
  }
  
  // Close on Escape
  const handleEscape = (e) => {
    if (e.key === 'Escape') {
      overlay.remove()
      window.removeEventListener('keydown', handleEscape)
    }
  }
  window.addEventListener('keydown', handleEscape)
  
  document.body.appendChild(overlay)
}

/**
 * Hook for specific component shortcuts
 * Example: Arrows to navigate tabs in detail panel
 */
export function useTabNavigation(tabs, activeTab, setActiveTab) {
  useEffect(() => {
    function handleKeyDown(e) {
      if (
        e.target.tagName === 'INPUT' ||
        e.target.tagName === 'TEXTAREA' ||
        e.target.isContentEditable
      ) {
        return
      }
      
      // Tab navigation with arrow keys (only when detail is open)
      if (!activeDetailTile.value) return
      
      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        const currentIndex = tabs.indexOf(activeTab)
        if (currentIndex === -1) return
        
        const delta = e.key === 'ArrowUp' ? -1 : 1
        const nextIndex = (currentIndex + delta + tabs.length) % tabs.length
        setActiveTab(tabs[nextIndex])
        
        e.preventDefault()
      }
    }
    
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [tabs, activeTab, setActiveTab])
}

/**
 * Visual indicator for available shortcuts
 * Shows hints on hover
 */
export function ShortcutHint({ shortcut, description, className = '' }) {
  return (
    <span className={`shortcut-hint ${className}`} title={description}>
      <kbd>{shortcut}</kbd>
    </span>
  )
}
